import { withAppContext } from '@/config/site'
import request from '@/utils/request'

/**
 * 检查是否需要参加考试
 * @param {object} params - 查询参数
 * @param {number} params.course_id - 课程ID
 * @param {number} params.target_type - 目标类型 1=章 2=节
 * @param {number} params.target_id - 目标ID（章/节ID）
 * @returns {Promise<object>} 考试检查结果
 */
export const checkExamRequired = async (params) => {
  const response = await request('/page/course/process/check', {
    data: withAppContext(params)
  })

  return response.data || {}
}

/**
 * 开始考试或继续未完成的考试
 * @param {number} processExamId - 教学流程考试配置ID
 * @returns {Promise<object>} 考试信息和题目列表
 */
export const startExam = async (processExamId) => {
  const response = await request('/page/course/process/start', {
    data: withAppContext({
      process_exam_id: processExamId
    })
  })

  return response.data || {}
}

/**
 * 提交考试答案
 * @param {object} data - 提交数据
 * @param {number} data.record_id - 考试记录ID
 * @param {array} data.answers - 答案列表
 * @returns {Promise<object>} 考试结果
 */
export const submitExam = async (data) => {
  const response = await request('/page/course/process/submit', {
    data: withAppContext(data)
  })

  return response.data || {}
}

/**
 * 查看考试结果详情
 * @param {number} recordId - 考试记录ID
 * @returns {Promise<object>} 考试结果详情
 */
export const fetchExamResult = async (recordId) => {
  const response = await request('/page/course/process/result', {
    data: withAppContext({
      record_id: recordId
    })
  })

  return response.data || {}
}

/**
 * 获取某场教学流程考试的历史记录
 * @param {object} params - 查询参数
 * @param {number} params.process_exam_id - 教学流程考试配置ID
 * @returns {Promise<array>} 考试记录列表
 */
export const fetchExamRecords = async (params = {}) => {
  const response = await request('/page/course/process/records', {
    data: withAppContext(params)
  })

  return Array.isArray(response.data) ? response.data : []
}

/**
 * 获取当前学员的全部考试记录
 * @param {object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.page_size - 每页条数
 * @param {number} [params.course_id] - 课程ID，不传或 0 表示全部课程
 * @param {number} [params.status] - 状态 0=答题中 1=已完成
 * @param {number} [params.result] - 结果 0=未判定 1=通过 2=未通过
 * @returns {Promise<{list: array, total: number, page: number, page_size: number}>}
 */
export const fetchMyExamRecords = async (params = {}) => {
  const payload = {
    page: 1,
    page_size: 10,
    ...params
  }
  const response = await request('/page/course/process/my-records', {
    data: withAppContext(payload)
  })
  const data = response.data || {}

  if (Array.isArray(data)) {
    return {
      list: data,
      total: data.length,
      page: payload.page,
      page_size: payload.page_size
    }
  }

  return {
    list: Array.isArray(data.list) ? data.list : [],
    total: Number(data.total) || 0,
    page: Number(data.page) || Number(payload.page) || 1,
    page_size: Number(data.page_size) || Number(payload.page_size) || 10
  }
}

/**
 * 获取考试记录详情
 * @param {number} recordId - 考试记录ID
 * @returns {Promise<object>} 考试记录详情
 */
export const fetchExamRecordDetail = async (recordId) => {
  return fetchExamResult(recordId)
}

/**
 * 获取课程教学流程树（包含章节、小节、课时和考试配置）
 * @param {number} courseId - 课程ID
 * @param {object} payload - 其他参数
 * @param {number} payload.with_lessons - 是否返回课时列表，默认1
 * @returns {Promise<object>} 教学流程树数据
 */
export const fetchCourseProcessTree = async (courseId, payload = {}) => {
  const response = await request('/page/course/process/index', {
    data: withAppContext({
      course_id: courseId,
      with_lessons: 1,
      ...payload
    })
  })

  return response.data || {}
}

/**
 * 获取课程教学流程统计信息
 * @param {number} courseId - 课程ID
 * @returns {Promise<object>} 流程统计数据
 */
export const fetchCourseProcessStats = async (courseId) => {
  const response = await fetchCourseProcessTree(courseId)

  return {
    chapter_count: response.chapter_count || 0,
    section_count: response.section_count || 0,
    lesson_count: response.lesson_count || 0,
    exam_count: response.exam_count || 0
  }
}

/**
 * 获取考试成绩分析
 * @param {number} recordId - 考试记录ID
 * @returns {Promise<object>} AI 成绩分析结果
 */
export const fetchExamScoreAnalysis = async (recordId) => {
  const response = await request('/page/course/process/result-analysis', {
    data: withAppContext({
      record_id: recordId
    })
  })

  return response.data || {}
}

